import { useEditor, EditorContent, type Editor as TiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { useEffect, type JSX } from 'react';
import type { TemplateDocument } from '@template-filler/core';
import { PlaceholderNode } from './PlaceholderNode.js';
import { toEditorContent } from './adapter.js';

// The editor's schema is deliberately narrower than TipTap's defaults: it must
// be able to produce only documents the allowlist accepts, or a user could
// compose something the server then refuses to save (FR-005, FR-011).
export const editorExtensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    // Disabled because the stored schema has no node for them. Leaving them on
    // would let a user write content that cannot be saved.
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    code: false,
    strike: false,
  }),
  Underline,
  PlaceholderNode,
];

interface EditorProps {
  content: TemplateDocument | undefined;
  onChange: (doc: TemplateDocument) => void;
  onReady?: (editor: TiptapEditor) => void;
}

export function Editor({ content, onChange, onReady }: EditorProps): JSX.Element {
  const editor = useEditor({
    extensions: editorExtensions,
    content: content ? toEditorContent(content) : { type: 'doc', content: [{ type: 'paragraph' }] },
    editorProps: {
      attributes: { class: 'editor-surface', 'aria-label': 'Template content' },
      // Paste from a word processor arrives with styling and tags the schema does
      // not have. ProseMirror coerces to the schema on paste, so unsupported
      // constructs are dropped here rather than being rejected at save time.
      transformPastedHTML: (html) => html,
    },
    onUpdate: ({ editor: instance }) => {
      onChange(instance.getJSON() as TemplateDocument);
    },
  });

  useEffect(() => {
    if (editor && onReady) onReady(editor);
  }, [editor, onReady]);

  return <EditorContent editor={editor} />;
}
